import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VIOLATION_REASONS, US_STATES } from '../constants';
import { getCurrentTimestampForInput } from '../utils/validation';
import { MapPicker } from '../components/MapPicker';
import { MapLocation } from '../types/maps';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import '../App.css';

interface IllegalParkingReport {
  licensePlate: string;
  state: string;
  address: string;
  latitude: number;
  longitude: number;
  reason: string;
  otherReason: string;
  description: string;
  timeObserved: string;
}

const MAX_PHOTOS = 3;

const ReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const photos = (location.state as { photos?: { url: string }[] })?.photos || [];
  
  // Handle both old and new data structures for backward compatibility
  const locationState = location.state as { 
    reportId?: string;
    reportData?: any;
    illegalParkingReport?: any;
  };
  
  // Use useMemo to prevent unnecessary re-renders
  const reportId = useMemo(() => 
    locationState?.reportId || locationState?.illegalParkingReport?.reportId,
    [locationState]
  );
  
  const reportData = useMemo(() => 
    locationState?.reportData || locationState?.illegalParkingReport || {},
    [locationState]
  );
  
  const [showReuploadAlert, setShowReuploadAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Combine all photos for display
  const allPhotos = photos.map(p => p.url);

  const [formData, setFormData] = useState<Omit<IllegalParkingReport, 'otherReason'>>({
    licensePlate: '',
    state: '',
    address: '',
    latitude: 0,
    longitude: 0,
    reason: '',
    description: '',
    timeObserved: getCurrentTimestampForInput()
  });

  const [otherReason, setOtherReason] = useState('');

  // Get user's current location for map default
  const { location: userLocation, loading: locationLoading, error: locationError } = useCurrentLocation();

  useEffect(() => {
    if (reportData) {
      setFormData({
        licensePlate: reportData.licensePlate || '',
        state: reportData.state || '',
        address: reportData.location?.address || '',
        latitude: reportData.latitude || 0,
        longitude: reportData.longitude || 0,
        reason: reportData.reason || '',
        description: reportData.description || '',
        timeObserved: reportData.timestamp || getCurrentTimestampForInput()
      });
      if (reportData.reason === 'other') {
        setOtherReason(reportData.otherReason || '');
      }
    }
  }, [reportData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'otherReason') {
      setOtherReason(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLocationSelect = (location: MapLocation) => {
    setFormData(prev => ({
      ...prev,
      address: location.formattedAddress || location.address || '',
      latitude: location.lat,
      longitude: location.lng
    }));
  };

  const handleReuploadClick = () => {
    setShowReuploadAlert(true);
  };

  const handleReuploadConfirm = () => {
    setShowReuploadAlert(false);
    navigate('/', { 
      state: { 
        message: 'You will be redirected to photo submission. Please upload your photos again.' 
      } 
    });
  };

  const handleReuploadCancel = () => {
    setShowReuploadAlert(false);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.licensePlate?.trim()) {
      alert('Please provide the license plate.');
      return;
    }
    if (!formData.state?.trim()) {
      alert('Please specify the State of license issued.');
      return;
    }
    if (!formData.reason?.trim()) {
      alert('Please select a violation type.');
      return;
    }
    if (!formData.address?.trim()) {
      alert('Please provide the location of the violation.');
      return;
    }

    // Set loading state
    setIsSubmitting(true);

    // Development mode detection
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

    try {
      const submissionData = {
        reportId: reportId,
        ...formData,
        // Include other reason if specified
        ...(formData.reason === 'other' && { otherReason })
      };

      console.log('Submitting data:', submissionData); // Debug log

      // In development, provide option to skip API call
      if (isDevelopment && !reportId) {
        console.warn('No reportId available - this might be a dev override flow');
        // Proceed to confirmation anyway in development
        navigate('/confirmation', { 
          state: { 
            formData: submissionData,
            photos: photos.map(p => ({ url: p.url }))
          } 
        });
        return;
      }

      const response = await fetch('https://y00ey592mh.execute-api.us-east-1.amazonaws.com/Production/illegal-parking-report', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      console.log('Response status:', response.status); // Debug log
      console.log('Response headers:', Object.fromEntries(response.headers)); // Debug log

      if (response.ok) {
        const responseData = await response.json();
        console.log('Response data:', responseData); // Debug log
        navigate('/confirmation', { 
          state: { 
            formData: submissionData,
            photos: photos.map(p => ({ url: p.url }))
          } 
        });
      } else {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        
        if (isDevelopment && (response.status === 500 || response.status === 0)) {
          if (window.confirm('API error occurred. Continue anyway? (Development mode)')) {
            navigate('/confirmation', { 
              state: { 
                formData: submissionData,
                photos: photos.map(p => ({ url: p.url }))
              } 
            });
            return;
          }
        }
        
        alert(`Failed to submit report (${response.status}). Please try again.`);
      }
    } catch (error) {
      console.error('Network Error:', error);
      
      // Development mode fallback for CORS/network issues
      if (isDevelopment) {
        console.warn('Network error in development mode:', error);
        if (window.confirm('Network error occurred. This might be a CORS issue. Continue anyway? (Development mode)')) {
          navigate('/confirmation', { 
            state: { 
              formData: {
                reportId: reportId,
                ...formData,
                ...(formData.reason === 'other' && { otherReason })
              },
              photos: photos.map(p => ({ url: p.url }))
            } 
          });
          return;
        }
      }
      
      // Check if it's a CORS error (common in development)
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        alert('Network error - this may be a CORS issue in development mode. Please check the console for details.');
      } else {
        alert('An error occurred while submitting the report. Please try again.');
      }
    } finally {
      // Reset loading state
      setIsSubmitting(false);
    }
  };

  // Only show missing data error if we have no reportId at all
  // Users can still fill out the form manually even if reportData is empty
  if (!reportId && !locationState?.reportId && !locationState?.illegalParkingReport?.reportId) {
    return (
      <div className="main-content">
        <p>Missing report data. Please return to home.</p>
        <button className="action-btn upload-btn" onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  const selectedReason = VIOLATION_REASONS.find(r => r.value === formData.reason);

  return (
    <div className="main-content">
      {/* Step indicator */}
      <div className="review-step-indicator">
        <div className="step-indicator">
          <div className="step completed">
            <div className="step-number">1</div>
            <div className="step-label">Upload</div>
          </div>
          <div className="step-connector"></div>
          <div className="step completed">
            <div className="step-number">2</div>
            <div className="step-label">Preview</div>
          </div>
          <div className="step-connector"></div>
          <div className="step active">
            <div className="step-number">3</div>
            <div className="step-label">Review</div>
          </div>
        </div>
      </div>

      <h1 className="main-title review-title">Review Your Report</h1>

      <form id="review-form" className="form-section">
        <div className="form-row">
          <label className="form-line flex-2">
            <span className="form-label">License Plate Number</span>
            <input
              name="licensePlate"
              value={formData.licensePlate}
              onChange={handleChange}
              className="text-input"
              placeholder="e.g. 7ABC123"
              required
            />
          </label>

          <label className="form-line flex-1">
            <span className="form-label">State</span>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="text-input"
              required
            >
              <option value="">Select State</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="form-line">
          <span className="form-label">Violation Type</span>
          <select
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            className="text-input"
            required
          >
            <option value="">Choose a violation type</option>
            {VIOLATION_REASONS.map(reason => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </select>
          {selectedReason?.description && (
            <p className="form-description">{selectedReason.description}</p>
          )}
        </label>

        {formData.reason === 'other' && (
          <label className="form-line">
            <span className="form-label">Please Specify</span>
            <input
              name="otherReason"
              value={otherReason}
              onChange={handleChange}
              className="text-input"
              placeholder="Describe the violation type"
              required
            />
          </label>
        )}

        <label className="form-line">
          <span className="form-label">Location</span>
          {locationLoading ? (
            <div className="map-loading" style={{ height: 250 }}>
              <div className="loading-spinner" />
              <p className="loading-text">Fetching your location...</p>
            </div>
          ) : (
            <>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={userLocation || undefined}
                height="250px"
                placeholder="Loading map..."
              />
              {locationError && (
                <p className="form-description" style={{ color: '#b71c1c', marginTop: 8 }}>{locationError}</p>
              )}
            </>
          )}
          <input
            name="geoLocation"
            value={formData.address}
            onChange={handleChange}
            className="text-input"
            placeholder="Address will be auto-populated from map selection"
            required
          />
        </label>

        <label className="form-line">
          <span className="form-label">Timestamp (EST)</span>
          <input
            name="timestamp"
            value={formData.timeObserved}
            onChange={handleChange}
            className="text-input"
            type="datetime-local"
            required
          />
        </label>

        <label className="form-line">
          <span className="form-label">Additional Details</span>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="text-input"
            placeholder="Provide any additional context about the violation... (Additional context can improve report approval rates with human reviewers)"
            rows={4}
          />
        </label>
      </form>

      {allPhotos.length > 0 && (
        <div className="review-photo-evidence">
          <h3 className="evidence-title">Photo Evidence</h3>
          <div className="evidence-description">
            <p>Review your uploaded photos to ensure they clearly show the violation.</p>
          </div>
          
          <div className="photo-evidence-container">
            <div className="photo-preview-grid review-mode">
              {allPhotos.map((photo, index) => (
                <div 
                  key={index} 
                  className={`photo-preview-item evidence-item ${currentPhotoIndex === index ? 'active' : ''}`}
                  onClick={() => setCurrentPhotoIndex(index)}
                >
                  <img src={photo} alt={`Violation evidence ${index + 1}`} />
                  <div className="photo-preview-overlay">
                    <div className="photo-number">{index + 1}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="evidence-photo-info">
              <p className="photo-count evidence-count">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M1 8a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 018.07 3h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 6H17a2 2 0 012 2v7a2 2 0 01-2 2H3a2 2 0 01-2-2V8zm13.5 3a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM10 14a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                {allPhotos.length} of {MAX_PHOTOS} photos attached
              </p>
            </div>
          </div>

          <div className="evidence-actions">
            <button
              type="button"
              className="action-btn reupload-btn"
              onClick={handleReuploadClick}
              disabled={isSubmitting}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm-3.068-9.849a7 7 0 00-11.712 3.138.75.75 0 001.449.39 5.5 5.5 0 019.201-2.466l.312.311h-2.433a.75.75 0 000 1.5h4.243a.75.75 0 00.75-.75V3.456a.75.75 0 00-1.5 0v2.43l-.31-.31z" clipRule="evenodd" />
              </svg>
              Reupload Photos
            </button>
          </div>
        </div>
      )}

      <div className="review-actions">
        <button
          id="submit-form"
          type="button"
          className="action-btn primary-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="button-spinner"></div>
              Submitting Report...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.896 28.896 0 003.105 2.289z" />
              </svg>
              Submit Report
            </>
          )}
        </button>
      </div>

      <p className="info-text" style={{ marginTop: '16px', textAlign: 'center' }}>
        Your identity will not be shared with the city or the violator. All reports are submitted anonymously.
      </p>

      {showReuploadAlert && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Reupload Photos</h3>
            <p>
              You will be redirected to the photo submission page. You'll need to go through 
              the entire process again, including photo upload and review.
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn secondary"
                onClick={handleReuploadCancel}
              >
                Cancel
              </button>
              <button
                className="modal-btn primary"
                onClick={handleReuploadConfirm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPage;
